<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading test...</p>
</div>

<!-- Error State -->
<div *ngIf="error && !isLoading" class="mw-700px mx-auto">
  <div class="alert alert-danger">
    <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
  </div>
  <button class="btn btn-outline-secondary" (click)="goBack()">
    <i class="bi bi-arrow-left me-2"></i>Back to Safety Tests
  </button>
</div>

<!-- Test Info / Start Screen -->
<div *ngIf="!isLoading && !error && test && !testStarted && !testCompleted" class="test-start-screen">
  <div class="mw-700px mx-auto">
    <div class="card">
      <div class="card-body p-4 p-md-5 text-center">
        <div class="test-icon mx-auto mb-4">
          <i [class]="'bi ' + (test.icon || 'bi-clipboard-check')"></i>
        </div>
        <h1 class="h2 mb-2">{{ test.title }}</h1>
        <p class="text-muted mb-4">{{ test.description }}</p>

        <div class="test-details mb-4">
          <div class="row g-3 justify-content-center">
            <div class="col-auto" *ngIf="test.questionCount">
              <div class="detail-item">
                <i class="bi bi-list-ol text-primary"></i>
                <span class="label">Questions</span>
                <span class="value">{{ test.questionCount }}</span>
              </div>
            </div>
            <div class="col-auto" *ngIf="test.timeLimit">
              <div class="detail-item">
                <i class="bi bi-clock text-primary"></i>
                <span class="label">Time Limit</span>
                <span class="value">{{ test.timeLimit }} min</span>
              </div>
            </div>
            <div class="col-auto">
              <div class="detail-item">
                <i class="bi bi-check-circle text-primary"></i>
                <span class="label">Passing Score</span>
                <span class="value">{{ test.passingScore }}%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="instructions-card mb-4">
          <h6 class="mb-3"><i class="bi bi-info-circle me-2"></i>Instructions</h6>
          <ul class="text-start mb-0">
            <li>Read each question carefully before selecting your answer</li>
            <li>You can navigate between questions using the navigation panel</li>
            <li>Flag questions you want to review later</li>
            <li *ngIf="test.timeLimit">The timer will start once you begin the test</li>
            <li *ngIf="test.timeLimit">Your test will be automatically submitted when time expires</li>
            <li>Your progress is saved automatically - you can resume if interrupted</li>
          </ul>
        </div>

        <div class="d-flex flex-column flex-sm-row gap-3 justify-content-center">
          <button class="btn btn-primary btn-lg" (click)="startTest()" [disabled]="isLoading">
            <i class="bi bi-play-fill me-2"></i>Start Test
          </button>
          <button class="btn btn-outline-secondary" (click)="goBack()">
            <i class="bi bi-arrow-left me-2"></i>Back to Safety Tests
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Test Taking Interface -->
<div *ngIf="!isLoading && !error && test && testStarted && !testCompleted" class="test-interface">
  <!-- Resumed Notice -->
  <div *ngIf="wasResumed" class="alert alert-info mb-3 mx-3">
    <i class="bi bi-info-circle me-2"></i>Resuming your previous attempt. Your saved answers have been restored.
  </div>

  <!-- Test Header -->
  <div class="test-header">
    <div class="d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-3">
        <h5 class="mb-0">{{ test.shortName || test.title }}</h5>
        <span class="badge bg-secondary">{{ currentQuestionIndex + 1 }} / {{ totalQuestions }}</span>
      </div>
      <div class="d-flex align-items-center gap-3">
        <div *ngIf="test.timeLimit" class="timer" [class.timer-warning]="timeWarning" [class.timer-critical]="timeCritical">
          <i class="bi bi-clock me-2"></i>
          <span class="time">{{ formattedTime }}</span>
        </div>
        <button class="btn btn-outline-secondary btn-sm" (click)="confirmExit()">
          <i class="bi bi-x-lg me-1"></i>Exit
        </button>
      </div>
    </div>
    <div class="progress mt-2 h-4px">
      <div class="progress-bar bg-primary" [style.width.%]="progressPercentage"></div>
    </div>
  </div>

  <div class="test-content">
    <div class="row g-4">
      <!-- Question Area -->
      <div class="col-lg-8">
        <div class="card question-card">
          <div class="card-body p-4">
            <!-- Question Header -->
            <div class="d-flex justify-content-between align-items-start mb-4">
              <div>
                <span *ngIf="currentQuestion?.type" class="badge bg-secondary mb-2 d-block">
                  {{ currentQuestion?.type === 'true_false' ? 'True/False' : 'Multiple Choice' }}
                </span>
                <h4 class="mb-0 fw-semibold">Question {{ currentQuestionIndex + 1 }}</h4>
              </div>
              <button class="btn btn-sm"
                      [class.btn-outline-warning]="!isQuestionFlagged(currentQuestionIndex)"
                      [class.btn-warning]="isQuestionFlagged(currentQuestionIndex)"
                      (click)="toggleFlag()" title="Flag for review">
                <i class="bi" [class.bi-flag]="!isQuestionFlagged(currentQuestionIndex)"
                   [class.bi-flag-fill]="isQuestionFlagged(currentQuestionIndex)"></i>
              </button>
            </div>

            <!-- Question Text -->
            <p class="question-text mb-4">{{ currentQuestion?.text }}</p>

            <!-- Answer Options -->
            <div class="answer-options">
              <div *ngFor="let option of currentQuestion?.options; let i = index"
                   class="answer-option"
                   [class.selected]="getSelectedAnswer() === option.id"
                   (click)="selectAnswer(option.id)">
                <span class="option-letter">{{ ['A', 'B', 'C', 'D', 'E', 'F'][i] }}</span>
                <span class="option-text">{{ option.text }}</span>
                <span class="option-check" *ngIf="getSelectedAnswer() === option.id">
                  <i class="bi bi-check-lg"></i>
                </span>
              </div>
            </div>
          </div>

          <!-- Question Navigation -->
          <div class="card-footer bg-transparent">
            <div class="d-flex justify-content-between align-items-center">
              <button class="btn btn-outline-secondary" (click)="previousQuestion()"
                      [disabled]="currentQuestionIndex === 0">
                <i class="bi bi-chevron-left me-1"></i>Previous
              </button>
              <div class="d-flex gap-2">
                <button *ngIf="currentQuestionIndex === totalQuestions - 1"
                        class="btn btn-primary" (click)="confirmSubmit()">
                  <i class="bi bi-check-lg me-1"></i>Submit Test
                </button>
                <button *ngIf="currentQuestionIndex < totalQuestions - 1"
                        class="btn btn-primary" (click)="nextQuestion()">
                  Next<i class="bi bi-chevron-right ms-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Panel -->
      <div class="col-lg-4">
        <!-- Progress Card -->
        <div class="card mb-3">
          <div class="card-header p-3">
            <h6 class="mb-0"><i class="bi bi-bar-chart me-2"></i>Progress</h6>
          </div>
          <div class="card-body">
            <div class="d-flex justify-content-between mb-2">
              <span class="small text-muted">Answered</span>
              <span class="small fw-medium">{{ answeredCount }} / {{ totalQuestions }}</span>
            </div>
            <div class="progress mb-3 h-8px">
              <div class="progress-bar bg-success" [style.width.%]="progressPercentage"></div>
            </div>
            <div class="d-flex gap-3 small">
              <span><span class="status-dot answered"></span> Answered: {{ answeredCount }}</span>
              <span><span class="status-dot unanswered"></span> Unanswered: {{ unansweredCount }}</span>
              <span><span class="status-dot flagged"></span> Flagged: {{ flaggedQuestions.size }}</span>
            </div>
          </div>
        </div>

        <!-- Question Navigator -->
        <div class="card">
          <div class="card-header p-3">
            <h6 class="mb-0"><i class="bi bi-grid-3x3-gap me-2"></i>Questions</h6>
          </div>
          <div class="card-body">
            <div class="question-nav-grid">
              <button *ngFor="let q of questions; let i = index"
                      class="question-nav-btn"
                      [class.current]="i === currentQuestionIndex"
                      [class.answered]="isQuestionAnswered(i)"
                      [class.flagged]="isQuestionFlagged(i)"
                      (click)="goToQuestion(i)">
                {{ i + 1 }}
                <i *ngIf="isQuestionFlagged(i)" class="bi bi-flag-fill flag-indicator"></i>
              </button>
            </div>
          </div>
          <div class="card-footer bg-transparent">
            <button class="btn btn-primary w-100" (click)="confirmSubmit()">
              <i class="bi bi-check-lg me-2"></i>Submit Test
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Test Completed Screen -->
<div *ngIf="!isLoading && !error && test && testCompleted && results" class="test-completed-screen">
  <div class="mw-700px mx-auto">
    <div class="card">
      <div class="card-body p-4 p-md-5 text-center">
        <div class="result-icon mx-auto mb-4" [class.passed]="results.passed" [class.failed]="!results.passed">
          <i class="bi" [class.bi-check-circle-fill]="results.passed" [class.bi-x-circle-fill]="!results.passed"></i>
        </div>

        <h1 class="h2 mb-2">{{ results.passed ? 'Congratulations!' : 'Test Not Passed' }}</h1>
        <p class="text-muted mb-4">
          {{ results.passed ? 'You have successfully passed the test.' : 'You did not achieve the required passing score.' }}
        </p>

        <div class="score-display mb-4" [class.score-passed]="results.passed" [class.score-failed]="!results.passed">
          <div class="score-value">{{ results.score }}%</div>
          <div class="score-label">Your Score</div>
          <div class="passing-info">Passing: {{ results.passingScore }}%</div>
        </div>

        <div class="result-stats mb-4">
          <div class="row g-3 justify-content-center">
            <div class="col-auto">
              <div class="stat-item">
                <span class="stat-value text-success">{{ results.correctAnswers }}</span>
                <span class="stat-label">Correct</span>
              </div>
            </div>
            <div class="col-auto">
              <div class="stat-item">
                <span class="stat-value text-danger">{{ results.incorrectAnswers }}</span>
                <span class="stat-label">Incorrect</span>
              </div>
            </div>
            <div class="col-auto">
              <div class="stat-item">
                <span class="stat-value">{{ results.timeSpent }}</span>
                <span class="stat-label">Time Used</span>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="results.passed && results.expiresAt" class="alert alert-success mb-4">
          <i class="bi bi-calendar-check me-2"></i>
          Your certification is valid until {{ results.expiresAt | date:'mediumDate' }}
        </div>

        <div class="d-flex flex-column flex-sm-row gap-3 justify-content-center">
          <button class="btn btn-primary btn-lg" (click)="viewReport()">
            <i class="bi bi-file-text me-2"></i>View Detailed Report
          </button>
          <button *ngIf="!results.passed" class="btn btn-outline-primary" (click)="retakeTest()">
            <i class="bi bi-arrow-repeat me-2"></i>Retake Test
          </button>
          <button class="btn btn-outline-secondary" (click)="goBack()">
            <i class="bi bi-arrow-left me-2"></i>Back to Safety Tests
          </button>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Submit Confirmation Modal -->
<div class="modal-backdrop" *ngIf="showSubmitConfirm" (click)="cancelSubmit()"></div>
<div class="confirm-modal" *ngIf="showSubmitConfirm">
  <div class="modal-content">
    <div class="modal-icon">
      <i class="bi bi-question-circle text-primary"></i>
    </div>
    <h5>Submit Test?</h5>
    <p class="text-muted">
      You have answered {{ answeredCount }} of {{ totalQuestions }} questions.
      <span *ngIf="unansweredCount > 0" class="text-warning d-block mt-1">
        <i class="bi bi-exclamation-triangle me-1"></i>{{ unansweredCount }} question(s) are unanswered.
      </span>
    </p>
    <div class="d-flex gap-2 justify-content-center">
      <button class="btn btn-outline-secondary" (click)="cancelSubmit()">Continue Test</button>
      <button class="btn btn-primary" (click)="submitTest()" [disabled]="isLoading">
        <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-1"></span>
        Submit Test
      </button>
    </div>
  </div>
</div>

<!-- Exit Confirmation Modal -->
<div class="modal-backdrop" *ngIf="showExitConfirm" (click)="cancelExit()"></div>
<div class="confirm-modal" *ngIf="showExitConfirm">
  <div class="modal-content">
    <div class="modal-icon">
      <i class="bi bi-exclamation-triangle text-warning"></i>
    </div>
    <h5>Exit Test?</h5>
    <p class="text-muted">
      Your progress has been saved. You can resume this test later if you exit now.
    </p>
    <div class="d-flex gap-2 justify-content-center">
      <button class="btn btn-outline-secondary" (click)="cancelExit()">Continue Test</button>
      <button class="btn btn-danger" (click)="exitTest()">Exit Test</button>
    </div>
  </div>
</div>
